import React, { useCallback, useEffect, useState } from 'react';
import { Autocomplete, CircularProgress, Paper, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import { debounceAsync } from '../utils/AsyncDebounce';

const AutocompleteAsync = ({
  getOptions,
  getSelectedOption,
  label,
  currentValue = '',
  isDisabled = false,
  isRequired = false,
  debounceTimer = 1000,
  normalizeOption = (option) => option,
  getOptionLabel = (option) => option,
  getOptionValue = (option) => option,
  onSelect = () => {},
  onClear = () => {},
  error,
  helperText,
}) => {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSelectedLoading, setIsSelectedLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  const debounceAsyncGet = debounceAsync(getOptions, debounceTimer);

  const searchOptions = useCallback(async(searchValue) => {
    try {
      setIsLoading(true);

      return await debounceAsyncGet(searchValue);
    } finally {
      setIsLoading(false);
    }
  }, [getOptions]);

  const searchSelectedOptions = useCallback(async(searchValue) => {
    try {
      setIsSelectedLoading(true);

      return await getSelectedOption(searchValue);
    } finally {
      setIsSelectedLoading(false);
    }
  }, [getSelectedOption]);

  const onSearch = useCallback(async(searchValue = '') => {
    const res = await searchOptions(searchValue);
    const normalizedOptions = res?.map((item) => normalizeOption(item));

    if (normalizedOptions) {
      setOptions(normalizedOptions);
    }
  }, [normalizeOption, searchOptions]);

  useEffect(() => {
    const selectedValue = getOptionValue(selectedOption) || '';

    if (currentValue && selectedValue !== currentValue) {
      searchSelectedOptions(currentValue).then((res) => {
        if (!res) {
          setInputValue(currentValue);

          return;
        }

        const normalizedOption = normalizeOption(res);

        setOptions([normalizedOption]);
        setSelectedOption(normalizedOption);
        setInputValue(getOptionLabel(normalizedOption));
      });
    }

    if (!currentValue && selectedValue) {
      setInputValue('');
      setSelectedOption(null);
    }
  }, [selectedOption, currentValue, getOptionLabel, getOptionValue, normalizeOption, searchSelectedOptions]);

  const onInputChange = (event, value, reason) => {
    if (event?.type === 'click' && reason === 'reset') {
      setInputValue(value);
    }

    if (reason === 'clear') {
      onClear();
    }

    if (reason === 'input') {
      setInputValue(value);
      onSearch(value);
    }
  };

  const onOpen = () => {
    if (!options.length) {
      onSearch(inputValue || '');
    }
  };

  const onSelectOption = (_, option = {}) => {
    setSelectedOption(option);
    setInputValue(getOptionLabel(option));
    onSelect(getOptionValue(option), option);
  };

  const onBlur = () => {
    setInputValue(getOptionLabel(selectedOption));
  };

  return (
    <Autocomplete
      id="autocomplete-select-async"
      fullWidth
      handleHomeEndKeys
      disabled={isDisabled || isSelectedLoading}
      value={selectedOption}
      inputValue={inputValue || ''}
      options={options}
      isOptionEqualToValue={(option, value) => getOptionValue(option) === getOptionValue(value)}
      onOpen={onOpen}
      onBlur={onBlur}
      onChange={onSelectOption}
      onInputChange={onInputChange}
      getOptionLabel={getOptionLabel}
      getOptionKey={getOptionValue}
      PaperComponent={({ children }) => (
        <Paper style={{ marginBottom: 10 }}>{children}</Paper>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          error={error}
          helperText={helperText}
          variant="outlined"
          required={isRequired}
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {(isLoading || isSelectedLoading) && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

AutocompleteAsync.propTypes = {
  getOptions: PropTypes.func.isRequired, // async (searchValue: string) => Options[]
  getSelectedOption: PropTypes.func.isRequired, // async (value: string) => Option
  label: PropTypes.string.isRequired, // localized string
  currentValue: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool,
  isRequired: PropTypes.bool,
  debounceTimer: PropTypes.number,
  normalizeOption: PropTypes.func, // should be memoized
  getOptionLabel: PropTypes.func, // should be memoized
  getOptionValue: PropTypes.func, // should be memoized
  onSelect: PropTypes.func, // (value: Value, option: Option) => void
  onClear: PropTypes.func, // () => void
  error: PropTypes.bool,
  helperText: PropTypes.string,
};

export default AutocompleteAsync;
